import React, { useEffect, useState } from 'react';
import { formatDate, formatDateTime } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldQuestion,
  Calendar,
  Building2,
  FileText,
  Hash,
  User,
  Clock,
  Stethoscope,
  DollarSign,
  Loader2,
} from 'lucide-react';
import api from '../../services/api';

/**
 * Rich eligibility result dialog. Surfaces every field the backend
 * `/api/patients/{id}/check-eligibility` returns (plan, coverage dates,
 * benefits, copays, registry cross-check) so the user can verify
 * coverage at a glance before submitting a claim.
 *
 * Designed to be data-tolerant: if the upstream payer doesn't return
 * some sections (e.g. no copays), those cards simply collapse.
 */

const STATUS_VARIANTS = {
  Active: {
    icon: CheckCircle2,
    label: 'Active Coverage',
    color: 'bg-emerald-500/15 text-emerald-700 border-emerald-300',
    dot: 'bg-emerald-500',
  },
  Inactive: {
    icon: XCircle,
    label: 'Coverage Inactive',
    color: 'bg-rose-500/15 text-rose-700 border-rose-300',
    dot: 'bg-rose-500',
  },
  Terminated: {
    icon: XCircle,
    label: 'Coverage Terminated',
    color: 'bg-rose-500/15 text-rose-700 border-rose-300',
    dot: 'bg-rose-500',
  },
  'Invalid ID': {
    icon: AlertTriangle,
    label: 'Invalid Medicaid ID',
    color: 'bg-amber-500/15 text-amber-700 border-amber-300',
    dot: 'bg-amber-500',
  },
  'Not Found': {
    icon: ShieldQuestion,
    label: 'Member Not Found',
    color: 'bg-slate-500/15 text-slate-700 border-slate-300',
    dot: 'bg-slate-500',
  },
};

const _formatDate = (v) => {
  if (!v) return '—';
  // accept yyyy-mm-dd or yyyymmdd
  const raw = String(v);
  if (/^\d{8}$/.test(raw)) {
    return formatDate(`${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`);
  }
  return formatDate(raw);
};

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-start gap-3 py-2">
    {Icon ? <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" /> : <div className="w-4" />}
    <div className="flex-1 min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 break-words ${mono ? 'font-mono' : 'font-medium'}`}>
        {value || <span className="text-slate-400 font-normal">Not provided</span>}
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
    <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
      {Icon ? <Icon className="h-4 w-4 text-slate-600" /> : null}
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
    </div>
    <div className="px-4 pb-2">{children}</div>
  </div>
);

const RegistryMismatchCard = ({ patient, result }) => {
  // Show only when the registry returned a comparison and any field differs.
  const ours = {
    first: (patient?.first_name || '').toUpperCase().trim(),
    last: (patient?.last_name || '').toUpperCase().trim(),
    dob: (patient?.date_of_birth || '').trim(),
    gender: (patient?.gender || '').toUpperCase().trim(),
  };
  const theirs = {
    first: (result?.registry_first_name || '').toUpperCase().trim(),
    last: (result?.registry_last_name || '').toUpperCase().trim(),
    dob: (result?.registry_dob || '').trim(),
    gender: (result?.registry_gender || '').toUpperCase().trim(),
  };
  const noRegistry = !theirs.first && !theirs.last && !theirs.dob && !theirs.gender;
  if (noRegistry) return null;

  const diffs = [];
  if (theirs.first && ours.first && theirs.first !== ours.first) {
    diffs.push(['First name', ours.first, theirs.first]);
  }
  if (theirs.last && ours.last && theirs.last !== ours.last) {
    diffs.push(['Last name', ours.last, theirs.last]);
  }
  if (theirs.dob && ours.dob && _formatDate(theirs.dob) !== _formatDate(ours.dob)) {
    diffs.push(['DOB', _formatDate(ours.dob), _formatDate(theirs.dob)]);
  }
  if (theirs.gender && ours.gender && theirs.gender !== ours.gender) {
    diffs.push(['Gender', ours.gender, theirs.gender]);
  }

  if (diffs.length === 0) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-sm text-emerald-900">
          <div className="font-semibold">Registry match</div>
          <div className="text-emerald-800/80 text-xs">
            Name, DOB, and gender on file match the ODM Medicaid registry.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-3"
      data-testid="eligibility-registry-mismatch"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-1">Registry mismatch — verify before billing</div>
          <div className="text-xs text-amber-800/80">
            ODM has a different value for the following fields. Claims may
            reject if patient demographics don&apos;t match the state registry.
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs">
        <div className="grid grid-cols-3 gap-2 px-2 py-1 font-semibold text-amber-900 border-b border-amber-200">
          <span>Field</span>
          <span>On file</span>
          <span>ODM registry</span>
        </div>
        {diffs.map(([field, mine, them]) => (
          <div
            key={field}
            className="grid grid-cols-3 gap-2 px-2 py-1.5 border-b border-amber-100 last:border-b-0"
          >
            <span className="text-amber-900 font-medium">{field}</span>
            <span className="text-amber-900/80 break-words">{mine || '—'}</span>
            <span className="text-amber-950 font-mono break-words">{them || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BenefitsList = ({ benefits }) => {
  if (!benefits || benefits.length === 0) return null;
  return (
    <SectionCard title="Benefit Details" icon={Stethoscope} testId="eligibility-benefits">
      <div className="divide-y divide-slate-100">
        {benefits.map((b, i) => (
          <div
            key={`${b.service_type || b.benefit_type || b.type || 'benefit'}-${i}`}
            className="py-2 flex items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">
                {b.service_type || b.benefit_type || b.type || 'Benefit'}
              </div>
              {b.coverage_level || b.description ? (
                <div className="text-xs text-slate-500 mt-0.5">
                  {[b.coverage_level, b.description].filter(Boolean).join(' • ')}
                </div>
              ) : null}
            </div>
            {b.status ? (
              <Badge
                variant="outline"
                className={
                  /active|cover/i.test(b.status)
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-700 border-slate-200'
                }
              >
                {b.status}
              </Badge>
            ) : null}
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const CopayList = ({ copays }) => {
  if (!copays || copays.length === 0) return null;
  return (
    <SectionCard title="Member Cost-Share" icon={DollarSign} testId="eligibility-copays">
      <div className="divide-y divide-slate-100">
        {copays.map((c, i) => (
          <div
            key={`${c.service_type || c.service || 'copay'}-${i}`}
            className="py-2 flex items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">
                {c.service_type || c.service || 'Service'}
              </div>
              {c.description ? (
                <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
              ) : null}
            </div>
            <div className="text-sm font-semibold font-mono text-slate-900 shrink-0">
              {c.copay_amount != null
                ? `$${Number(c.copay_amount).toFixed(2)}`
                : c.copay || '—'}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

export const EligibilityResultDialog = ({ open, onClose, result, patient, onRecheck, rechecking }) => {
  // P48 — On-demand SFTP eligibility: when the backend returns
  // `state: "polling"`, the actual 271 hasn't arrived yet. Poll the
  // status endpoint every 5 sec until terminal state, then re-render
  // with the resolved data.
  const [polledResult, setPolledResult] = useState(null);
  const isPolling = (polledResult?.state || result?.state) === 'polling';

  useEffect(() => {
    if (!open) {
      setPolledResult(null);
      return undefined;
    }
    if (result?.state !== 'polling' || !result?.run_id || !patient?.id) {
      setPolledResult(null);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(
          `/patients/${patient.id}/check-eligibility/status/${result.run_id}`,
        );
        if (cancelled) return;
        setPolledResult(r.data);
        if (r.data?.state && r.data.state !== 'polling') {
          clearInterval(handle);
        }
      } catch {
        // Silent — next tick will retry. If the run is gone, the
        // status endpoint will 404 and we just stop showing.
      }
    };
    // Fire the first poll immediately so the timer doesn't sit at 0
    // for a full interval before reflecting backend state.
    tick();
    const handle = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [open, result?.run_id, result?.state, patient?.id]);

  if (!open || !result) return null;

  // Choose the most up-to-date payload: polled result wins once we
  // have one; otherwise show the initial response (which may itself
  // already be terminal — instant cached path).
  const effective = polledResult || result;

  const statusKey = effective.eligibility_status || (effective.is_eligible ? 'Active' : 'Inactive');
  const variant = STATUS_VARIANTS[statusKey] || STATUS_VARIANTS.Inactive;
  const StatusIcon = variant.icon;

  const patientName = [patient?.first_name, patient?.last_name].filter(Boolean).join(' ').trim()
    || effective.member_name
    || '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="eligibility-result-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Eligibility Verification</span>
            {effective.mode === 'mock' ? (
              <Badge
                variant="outline"
                className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] uppercase tracking-wider"
              >
                Mock mode
              </Badge>
            ) : null}
            {effective.mode === 'sftp_live' ? (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider"
              >
                Live ODM
              </Badge>
            ) : null}
            {effective.mode === 'p40_batch' ? (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] uppercase tracking-wider"
              >
                Cached
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {patientName}
            {patient?.medicaid_id ? (
              <span className="text-slate-400">  •  Medicaid ID </span>
            ) : null}
            {patient?.medicaid_id ? (
              <span className="font-mono text-slate-700">{patient.medicaid_id}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {isPolling ? (
          <PollingBanner result={effective} />
        ) : (
          <>
            {/* Big status banner */}
            <div
              className={`rounded-lg border px-4 py-3 flex items-center gap-3 ${variant.color}`}
              data-testid="eligibility-status-banner"
            >
              <span className={`h-2.5 w-2.5 rounded-full ${variant.dot}`} />
              <StatusIcon className="h-5 w-5" />
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold" data-testid="eligibility-status-label">
                  {variant.label}
                </div>
                {effective.status_message ? (
                  <div className="text-sm opacity-80 mt-0.5">{effective.status_message}</div>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
              {/* Plan & Coverage */}
              <SectionCard title="Plan & Coverage" icon={Building2} testId="eligibility-plan">
                <InfoRow icon={Building2} label="Payer" value={effective.payer_name} />
                <InfoRow icon={Hash} label="Payer ID" value={effective.payer_id} mono />
                <InfoRow icon={FileText} label="Plan Name" value={effective.plan_name} />
                <InfoRow icon={FileText} label="Plan Type" value={effective.plan_type} />
                <InfoRow icon={FileText} label="Coverage Type" value={effective.coverage_type} />
              </SectionCard>

              {/* Dates & Member */}
              <SectionCard title="Member & Dates" icon={User} testId="eligibility-member">
                <InfoRow icon={User} label="Member Name" value={effective.member_name || patientName} />
                <InfoRow icon={Calendar} label="Service Date" value={_formatDate(effective.service_date)} />
                <InfoRow
                  icon={Calendar}
                  label="Effective Date"
                  value={_formatDate(effective.effective_date)}
                />
                <InfoRow
                  icon={Calendar}
                  label="Termination Date"
                  value={_formatDate(effective.termination_date)}
                />
              </SectionCard>
            </div>

            <RegistryMismatchCard patient={patient} result={effective} />

            <BenefitsList benefits={effective.benefit_details} />
            <CopayList copays={effective.copay_info} />

            {/* Footer / audit info */}
            <Separator />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <Hash className="h-3.5 w-3.5" />
                <span>Trace #</span>
                <span className="font-mono text-slate-700">{effective.trace_number || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                <span>Verified</span>
                <span className="font-mono text-slate-700">
                  {effective.verification_date
                    ? formatDateTime(effective.verification_date)
                    : effective.completed_at
                      ? formatDateTime(effective.completed_at)
                      : '—'}
                </span>
              </div>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {onRecheck && !isPolling ? (
            <Button
              variant="outline"
              onClick={onRecheck}
              disabled={rechecking}
              data-testid="eligibility-recheck-btn"
            >
              {rechecking ? 'Re-checking…' : 'Re-check now'}
            </Button>
          ) : null}
          <Button onClick={onClose} data-testid="eligibility-close-btn">
            {isPolling ? 'Run in background' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * P48 polling banner — shown while we're waiting for ODM to drop the
 * 271 file in the SFTP outbound directory. Renders an animated
 * spinner, an elapsed-seconds counter ticking once per second, and a
 * preview of the trace + active environment so the user has visible
 * proof we actually called ODM (and which env).
 */
const PollingBanner = ({ result }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(h);
  }, []);
  // Prefer the server-reported elapsed seconds when present (more
  // accurate because it survives dialog close/reopen).
  const baseSeconds = result?.elapsed_seconds || 0;
  const total = baseSeconds + tick;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const envBadge = result?.active_environment === 'prod' ? 'PROD' : 'CERT';
  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 flex items-start gap-3"
      data-testid="eligibility-polling-banner"
    >
      <Loader2 className="h-5 w-5 text-amber-700 animate-spin mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-base font-semibold text-amber-900">
          Querying Ohio Medicaid (live, {envBadge})…
        </div>
        <div className="text-sm text-amber-800 mt-0.5">
          {result?.status_message || 'Waiting for ODM\u2019s 271 response. Typically 30 sec – 3 min.'}
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-amber-700">
          <span className="font-mono">
            Elapsed: {mins}:{String(secs).padStart(2, '0')}
          </span>
          {result?.trace_number ? (
            <span className="font-mono truncate" title={result.trace_number}>
              TRN {result.trace_number.slice(-12)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EligibilityResultDialog;
